import { jsPDF } from 'jspdf'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'

// CSV opens directly in Excel/Sheets; XLSX (SheetJS) is used only for the
// multi-tab HQ exports; PDF via jsPDF. All exports are client-side.

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// A hyperlinked cell: pass { text, link } wherever a row cell is expected and
// the exporters make it a real link — Excel writes a hyperlink, the PDF draws a
// clickable annotation over the text. Anything else just reads the text, so a
// row is safe to hand to any exporter (2026-08-25).
export const linkCell = (link, text = 'view') => (link ? { text, link } : '')

const cellText = (c) =>
  c && typeof c === 'object' && c.text !== undefined ? c.text : c

const cellLink = (c) => (c && typeof c === 'object' && c.link ? c.link : null)

// A cell holding several links: one per '\n'-separated line of its text.
const cellLinks = (c) =>
  c && typeof c === 'object' && Array.isArray(c.links) && c.links.length > 1
    ? c.links
    : null

// headers: string[]; rows: Array<Array<string|number>>
export const downloadCsv = (filename, headers, rows) => {
  const csv = Papa.unparse({ fields: headers, data: rows })
  const name = filename.endsWith('.csv') ? filename : `${filename}.csv`
  // BOM so Excel reads UTF-8 (₹, etc.) correctly
  triggerDownload(
    new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
    name
  )
}

// Draw one gridded table (title + bordered/striped/wrapped rows, header repeats
// on page breaks) starting at `y`; returns the y after it. Column widths are
// proportional to each table's own content, so multiple tables can share a doc.
const drawTableSection = (doc, title, headers, rows, y) => {
  const marginX = 24
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const usableW = pageW - marginX * 2
  const fontSize = 7.5
  const lineH = 9
  const padX = 3
  const ncol = Math.max(headers.length, 1)

  const weight = headers.map((h, i) => {
    let m = String(h == null ? '' : h).length
    rows.forEach((r) => {
      const t = cellText(r[i])
      m = Math.max(m, String(t == null ? '' : t).length)
    })
    return Math.max(4, Math.min(m, 26))
  })
  const wsum = weight.reduce((a, b) => a + b, 0) || 1
  const colW = weight.map((w) => (w / wsum) * usableW)
  const colX = []
  let x = marginX
  for (let i = 0; i < ncol; i++) {
    colX[i] = x
    x += colW[i]
  }

  const wrap = (v, i) => {
    const t = cellText(v)
    return doc.splitTextToSize(String(t == null ? '' : t), colW[i] - padX * 2)
  }

  if (title) {
    doc.setFontSize(13)
    doc.setFont('helvetica', 'bold')
    doc.text(title, marginX, y)
    y += 16
  }
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(fontSize)

  const drawRow = (cells, { head, stripe } = {}) => {
    const lines = cells.map((c, i) => wrap(c, i))
    const nLines = Math.max(1, ...lines.map((l) => l.length))
    const rowH = nLines * lineH + 4
    if (y + rowH > pageH - 22) {
      doc.addPage()
      y = 30
      drawRow(headers, { head: true })
    }
    if (head) {
      doc.setFillColor(235, 238, 243)
      doc.rect(marginX, y, usableW, rowH, 'F')
      doc.setFont('helvetica', 'bold')
    } else if (stripe) {
      doc.setFillColor(249, 249, 246)
      doc.rect(marginX, y, usableW, rowH, 'F')
    }
    doc.setDrawColor(214)
    lines.forEach((ln, i) => {
      doc.rect(colX[i], y, colW[i], rowH)
      const url = head ? null : cellLink(cells[i])
      ln.forEach((t, li) => {
        const ty = y + lineH - 1 + li * lineH
        if (url) doc.textWithLink(t, colX[i] + padX, ty, { url })
        else doc.text(t, colX[i] + padX, ty)
      })
    })
    if (head) doc.setFont('helvetica', 'normal')
    y += rowH
  }

  drawRow(headers, { head: true })
  rows.forEach((row, ri) => drawRow(row, { stripe: ri % 2 === 1 }))
  return y
}

// A proper gridded table (P4, 2026-07-21): reads like the on-screen table.
export const downloadPdf = (filename, title, headers, rows) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  drawTableSection(doc, title, headers, rows, 30)
  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`)
}

// An ORDER document rather than a table dump (2026-08-05). The register export
// printed all 22 sheet columns, so `Type` and `Rate` were squeezed to three
// characters, Drive URLs wrapped over five lines, and four columns repeated one
// value on every row. Here the repeating values become a header block, the PO
// sits top-right where a vendor looks for it, and the numbers total at the foot.
//
// spec: { title, subtitle, po, meta: [{k,v}], headers, rows, align: bool[],
//         totals: (string|null)[], footNote }
// Renders one order starting on the doc's CURRENT page; footers (PO + page
// numbers) cover only this order's pages, so several orders can share a file
// and each still reads as its own document.
export const renderOrder = (doc, spec) => {
  const startPage = doc.internal.getNumberOfPages()
  const marginX = 28
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const usableW = pageW - marginX * 2
  const ink = [26, 23, 20]
  const muted = [120, 112, 104]
  const accent = [242, 94, 53]
  let y = 34

  // --- header -------------------------------------------------------------
  doc.setTextColor(...ink)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text(spec.title || 'Purchase Order', marginX, y)
  if (spec.po) {
    doc.setFontSize(13)
    doc.text(spec.po, pageW - marginX, y, { align: 'right' })
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.5)
    doc.setTextColor(...muted)
    doc.text('PO NUMBER', pageW - marginX, y - 11, { align: 'right' })
  }
  y += 13
  if (spec.subtitle) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    doc.setTextColor(...muted)
    doc.text(spec.subtitle, marginX, y)
    y += 12
  }
  doc.setDrawColor(...accent)
  doc.setLineWidth(1)
  doc.line(marginX, y, pageW - marginX, y)
  doc.setLineWidth(0.5)
  y += 14

  // --- meta band: the columns that held one value on every row ------------
  const meta = (spec.meta || []).filter((m) => m && m.v)
  if (meta.length) {
    const perRow = 4
    const colW = usableW / perRow
    const rowsOfMeta = Math.ceil(meta.length / perRow)
    const bandH = rowsOfMeta * 22 + 6
    doc.setFillColor(246, 243, 239)
    doc.rect(marginX, y - 4, usableW, bandH, 'F')
    meta.forEach((m, i) => {
      const cx = marginX + (i % perRow) * colW + 6
      const cy = y + Math.floor(i / perRow) * 22
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(6.5)
      doc.setTextColor(...muted)
      doc.text(String(m.k).toUpperCase(), cx, cy + 6)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8.5)
      doc.setTextColor(...ink)
      doc.text(String(m.v), cx, cy + 16)
    })
    y += bandH + 8
  }

  // --- subset note: this document is a cut of the PO, and says so ---------
  if (spec.note) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8.5)
    doc.setTextColor(...accent)
    doc.text(String(spec.note), marginX, y + 2)
    doc.setTextColor(...ink)
    y += 16
  }

  // --- line table ---------------------------------------------------------
  const headers = spec.headers || []
  const rows = spec.rows || []
  const align = spec.align || []
  const fontSize = 7.5
  const lineH = 9
  const padX = 4

  // Width by content, but never below what the header itself needs — a purely
  // proportional split squeezed short-content columns until `Rate` and `GST`
  // wrapped to two lines, which is the flaw this export exists to fix. Each
  // column is floored at its measured header width; the slack left over is
  // what gets shared out proportionally.
  const weight = headers.map((h, i) => {
    let m = String(h == null ? '' : h).length
    rows.forEach((r) => {
      const t = cellText(r[i])
      m = Math.max(m, String(t == null ? '' : t).length)
    })
    return Math.max(4, Math.min(m, 30))
  })
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6.8)
  const minW = headers.map(
    (h) => doc.getTextWidth(String(h == null ? '' : h)) + padX * 2 + 2
  )
  const minSum = minW.reduce((a, b) => a + b, 0)
  const wsum = weight.reduce((a, b) => a + b, 0) || 1
  const slack = usableW - minSum
  const colW =
    slack > 0
      ? weight.map((w, i) => minW[i] + (w / wsum) * slack)
      : weight.map((w) => (w / wsum) * usableW)
  const colX = []
  let cx0 = marginX
  headers.forEach((_, i) => {
    colX[i] = cx0
    cx0 += colW[i]
  })

  const drawRow = (cells, opts = {}) => {
    const lines = cells.map((c, i) => {
      const t = cellText(c)
      return doc.splitTextToSize(String(t == null ? '' : t), colW[i] - padX * 2)
    })
    const nLines = Math.max(1, ...lines.map((l) => l.length))
    const rowH = nLines * lineH + 5
    if (y + rowH > pageH - 46) {
      doc.addPage()
      y = 34
      drawRow(headers, { head: true })
    }
    if (opts.head) {
      doc.setFillColor(240, 237, 232)
      doc.rect(marginX, y, usableW, rowH, 'F')
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...muted)
      doc.setFontSize(6.8)
    } else if (opts.total) {
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...ink)
      doc.setFontSize(fontSize)
      doc.setDrawColor(150)
      doc.line(marginX, y, pageW - marginX, y)
      doc.setDrawColor(214)
    } else {
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...ink)
      doc.setFontSize(fontSize)
      if (opts.stripe) {
        doc.setFillColor(250, 249, 247)
        doc.rect(marginX, y, usableW, rowH, 'F')
      }
    }
    lines.forEach((ln, i) => {
      const right = align[i] === 'right'
      // A link cell prints in link blue and carries the annotation, so the PO
      // that reaches a vendor opens the product page it was priced from.
      const plain = opts.head || opts.total
      const one = plain ? null : cellLink(cells[i])
      // Several links in one cell: each printed line opens its own file. A
      // label that wrapped keeps the link of the line it wrapped from.
      const many = plain ? null : cellLinks(cells[i])
      const urlOf = many
        ? String(cellText(cells[i]))
            .split('\n')
            .flatMap((part, k) =>
              doc
                .splitTextToSize(part, colW[i] - padX * 2)
                .map(() => many[k] || one)
            )
        : null
      if (one) doc.setTextColor(21, 101, 192)
      ln.forEach((t, li) => {
        const ty = y + lineH - 1 + li * lineH
        const url = urlOf ? urlOf[li] || one : one
        if (url) doc.textWithLink(t, colX[i] + padX, ty, { url })
        else if (right)
          doc.text(t, colX[i] + colW[i] - padX, ty, { align: 'right' })
        else doc.text(t, colX[i] + padX, ty)
      })
      if (one) doc.setTextColor(...ink)
    })
    if (!opts.head && !opts.total) {
      doc.setDrawColor(228)
      doc.line(marginX, y + rowH, pageW - marginX, y + rowH)
    }
    y += rowH
  }

  drawRow(headers, { head: true })
  rows.forEach((r, i) => drawRow(r, { stripe: i % 2 === 1 }))
  if (spec.totals) drawRow(spec.totals, { total: true })

  // --- the full order's numbers, when the table above is only part of it --
  if (spec.postNote) {
    y += 14
    if (y > pageH - 60) {
      doc.addPage()
      y = 34
    }
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(...ink)
    doc.text(String(spec.postNote), marginX, y)
  }

  // --- sign-off + footer on every page ------------------------------------
  y += 26
  if (spec.signoff !== false && y < pageH - 60) {
    const sigW = usableW / 3
    ;['Requested by', 'Approved by', 'Vendor acknowledgement'].forEach(
      (label, i) => {
        const sx = marginX + i * sigW
        doc.setDrawColor(190)
        doc.line(sx, y, sx + sigW - 24, y)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(6.8)
        doc.setTextColor(...muted)
        doc.text(label, sx, y + 9)
      }
    )
  }

  const endPage = doc.internal.getNumberOfPages()
  const pages = endPage - startPage + 1
  for (let p = startPage; p <= endPage; p++) {
    doc.setPage(p)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.8)
    doc.setTextColor(...muted)
    if (spec.po) doc.text(spec.po, marginX, pageH - 16)
    if (spec.footNote) {
      doc.text(spec.footNote, pageW / 2, pageH - 16, { align: 'center' })
    }
    doc.text(
      `Page ${p - startPage + 1} of ${pages}`,
      pageW - marginX,
      pageH - 16,
      {
        align: 'right'
      }
    )
  }
  doc.setPage(endPage)
}

export const downloadOrderPdf = (filename, spec) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  renderOrder(doc, spec)
  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`)
}

// One file, one order document per centre — each on its own page(s).
export const downloadOrderPdfSections = (filename, specs) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  specs.forEach((spec, i) => {
    if (i > 0) doc.addPage()
    renderOrder(doc, spec)
  })
  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`)
}

// Multi-page PDF: the first section (summary) on page 1, then each following
// section (one per centre) starts on its own page. sections: [{title,headers,rows}].
export const downloadPdfSections = (filename, sections) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  sections.forEach((s, i) => {
    if (i > 0) doc.addPage()
    drawTableSection(doc, s.title, s.headers, s.rows, 30)
  })
  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`)
}

// Multi-tab Excel: sheets = [{name, headers, rows, pre}] -> one worksheet
// each. `pre` rows (e.g. a PO/centre/month header block) land ABOVE the
// table's header row.
export const downloadXlsxSheets = (filename, sheets) => {
  const wb = XLSX.utils.book_new()
  const used = {}
  sheets.forEach((s, i) => {
    const aoa = [...(s.pre || []), s.headers || [], ...(s.rows || [])]
    const ws = XLSX.utils.aoa_to_sheet(
      aoa.map((row) => (row || []).map(cellText))
    )
    // Re-attach the links the flattening above dropped, so the item column is
    // clickable in Excel/Sheets rather than a bare word.
    aoa.forEach((row, r) =>
      (row || []).forEach((c, col) => {
        const url = cellLink(c)
        if (!url) return
        const cell = ws[XLSX.utils.encode_cell({ r, c: col })]
        if (cell) cell.l = { Target: url, Tooltip: 'Open the item link' }
      })
    )
    // Sheet names: <=31 chars, no \ / ? * [ ] :, and unique within the workbook.
    let base =
      (s.name || `Sheet${i + 1}`)
        .replace(/[\\/?*[\]:]/g, ' ')
        .slice(0, 28)
        .trim() || `Sheet${i + 1}`
    let name = base
    let n = 2
    while (used[name.toLowerCase()]) {
      name = `${base.slice(0, 25)} ${n++}`
    }
    used[name.toLowerCase()] = true
    XLSX.utils.book_append_sheet(wb, ws, name)
  })
  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`)
}

// Parse an uploaded CSV File into row objects keyed by header. Promise-based.
export const parseCsvFile = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => (h || '').trim(),
      complete: (res) => resolve(res.data || []),
      error: (err) => reject(err)
    })
  })
